import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtINR, fmtDate } from "@/lib/format";
import { API } from "@/lib/api";
import {
  Car, User, FileText, ShieldCheck, Wrench, Truck, ScrollText,
  Bell, ClipboardCheck, XCircle, Coins, IndianRupee, CheckCircle2, Download,
} from "lucide-react";

const EVENT_ICON = {
  lead:              { Icon: User,          color: "text-blue-500",    bg: "bg-blue-500/10" },
  quote:             { Icon: FileText,      color: "text-indigo-500",  bg: "bg-indigo-500/10" },
  booking:           { Icon: ClipboardCheck,color: "text-emerald-600", bg: "bg-emerald-500/10" },
  allocation:        { Icon: Car,           color: "text-purple-600",  bg: "bg-purple-500/10" },
  finance:           { Icon: IndianRupee,   color: "text-amber-600",   bg: "bg-amber-500/10" },
  insurance:         { Icon: ShieldCheck,   color: "text-cyan-600",    bg: "bg-cyan-500/10" },
  rto:               { Icon: ScrollText,    color: "text-orange-600",  bg: "bg-orange-500/10" },
  delivery:          { Icon: Truck,         color: "text-emerald-700", bg: "bg-emerald-600/10" },
  invoice:           { Icon: FileText,      color: "text-teal-600",    bg: "bg-teal-500/10" },
  gate_pass:         { Icon: CheckCircle2,  color: "text-lime-600",    bg: "bg-lime-500/10" },
  cancellation:      { Icon: XCircle,       color: "text-red-500",     bg: "bg-red-500/10" },
  job_card:          { Icon: Wrench,        color: "text-slate-600",   bg: "bg-slate-500/10" },
  reminder:          { Icon: Bell,          color: "text-yellow-600",  bg: "bg-yellow-500/10" },
  voucher_receipt:   { Icon: Coins,         color: "text-emerald-600", bg: "bg-emerald-500/10" },
  voucher_sales:     { Icon: FileText,      color: "text-teal-600",    bg: "bg-teal-500/10" },
  voucher_journal:   { Icon: FileText,      color: "text-slate-500",   bg: "bg-slate-500/10" },
};

export default function VehicleLifecycleTab() {
  const [bookings, setBookings] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [q, setQ] = useState("");
  const [lifecycle, setLifecycle] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/dealership/bookings").then((r) => {
      setBookings(r.data || []);
      if ((r.data || []).length && !selectedId) {
        setSelectedId(r.data[0].id);
      }
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    api.get(`/dealership/vehicle-lifecycle/${selectedId}`)
      .then((r) => setLifecycle(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load lifecycle"))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return bookings;
    return bookings.filter((b) => {
      const c = b.customer || {};
      const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
      return (b.booking_number || "").toLowerCase().includes(qq)
        || name.includes(qq)
        || (b.allocated_vin || "").toLowerCase().includes(qq)
        || (c.mobile || "").includes(qq);
    });
  }, [bookings, q]);

  // Iter-163 · Download PDF export of the timeline
  const downloadPdf = async () => {
    if (!selectedId) return;
    try {
      const res = await fetch(`${API}/dealership/vehicle-lifecycle/${selectedId}/pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vehicle360_${lifecycle?.booking?.booking_number || "booking"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Failed to download PDF");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" data-testid="vehicle-lifecycle-tab">
      {/* LEFT · Search / picker */}
      <div className="card-sharp p-3 h-[calc(100vh-260px)] overflow-y-auto">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Pick a booking</div>
        <Input placeholder="Search booking / VIN / customer / mobile"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 className="rounded-sm mb-3" data-testid="vl-search" />
        <div className="space-y-1">
          {filtered.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">No bookings match</div>}
          {filtered.map((b) => {
            const c = b.customer || {};
            const active = b.id === selectedId;
            return (
              <button key={b.id} onClick={() => setSelectedId(b.id)}
                        className={`w-full text-left px-2.5 py-2 rounded-sm border transition-colors ${active ? "bg-accent/10 border-accent" : "border-border hover:bg-muted/40"}`}
                        data-testid={`vl-booking-${b.booking_number}`}>
                <div className="font-mono text-[11px] flex items-center gap-1.5">
                  <Car className={`w-3 h-3 ${active ? "text-accent" : "text-muted-foreground"}`} />
                  {b.booking_number}
                </div>
                <div className="text-xs mt-0.5 truncate">{c.first_name} {c.last_name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {(b.vehicle_model || {}).name || "—"} · {b.allocated_vin || "no VIN"}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* RIGHT · Timeline */}
      <div className="space-y-4">
        {!lifecycle && !loading && (
          <div className="card-sharp p-10 text-center text-muted-foreground text-sm">Select a booking to see its complete lifecycle</div>
        )}
        {loading && (
          <div className="card-sharp p-10 text-center text-muted-foreground text-sm">Loading lifecycle…</div>
        )}
        {lifecycle && (
          <>
            {/* Header card */}
            <div className="card-sharp p-5" data-testid="vl-header">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Vehicle 360 · Lifecycle</div>
                  <div className="metric-value text-2xl mt-1">{lifecycle.booking.booking_number}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {(lifecycle.booking.customer || {}).first_name} {(lifecycle.booking.customer || {}).last_name}
                    &nbsp;·&nbsp;
                    {(lifecycle.booking.vehicle_model || {}).name || "—"} {(lifecycle.booking.vehicle_model || {}).variant || ""}
                    {lifecycle.booking.allocated_vin && <>&nbsp;·&nbsp;<span className="font-mono">{lifecycle.booking.allocated_vin}</span></>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">On-road price</div>
                  <div className="text-xl tabular-nums font-semibold">{fmtINR(lifecycle.booking.on_road_price || 0)}</div>
                  <div className="mt-1 flex gap-1 justify-end">
                    <span className="px-2 py-0.5 text-[10px] rounded-sm bg-muted font-mono uppercase">{lifecycle.booking.booking_status || "—"}</span>
                    <span className="px-2 py-0.5 text-[10px] rounded-sm bg-muted font-mono uppercase">{lifecycle.booking.delivery_status || "—"}</span>
                  </div>
                  <Button onClick={downloadPdf} size="sm" className="mt-3 rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5 h-8" data-testid="vl-download-pdf">
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </Button>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="card-sharp p-4 relative">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
                Timeline · {lifecycle.event_count} events
              </div>
              {lifecycle.events.length === 0 && (
                <div className="text-center text-muted-foreground text-sm py-6">No events yet</div>
              )}
              <div className="relative pl-8">
                <div className="absolute left-3 top-1 bottom-1 w-px bg-border" />
                {lifecycle.events.map((e, idx) => {
                  const meta = EVENT_ICON[e.kind] || EVENT_ICON.booking;
                  const Icon = meta.Icon;
                  return (
                    <div key={idx} className="relative py-3" data-testid={`vl-event-${e.kind}-${idx}`}>
                      <div className={`absolute -left-8 top-3 w-6 h-6 rounded-sm ${meta.bg} flex items-center justify-center`}>
                        <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                      </div>
                      <div className="border rounded-sm p-3 bg-background hover:bg-muted/20 transition-colors">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{e.title}</div>
                            <div className="text-xs text-muted-foreground truncate">{e.meta || "—"}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[10px] font-mono text-muted-foreground">{fmtDate(e.date)}</div>
                            {e.status && (
                              <div className="text-[10px] font-mono uppercase mt-0.5 tracking-wider px-1.5 py-0.5 border rounded-sm inline-block">{String(e.status).replaceAll('_',' ')}</div>
                            )}
                            {e.amount && (
                              <div className="text-xs tabular-nums font-medium mt-0.5">{fmtINR(e.amount)}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
